/**
 * 音频播放器本地存储管理器
 * 用于保存和恢复音频播放状态
 */

const STORAGE_KEY = 'vue_audio_player_state';
const STORAGE_VERSION = '1.0';

class AudioStorage {
  constructor() {
    this.storageKey = STORAGE_KEY;
    this.version = STORAGE_VERSION;
  }

  /**
   * 保存音频播放状态到本地存储
   * @param {Object} audioState - 音频状态对象
   */
  saveAudioState(audioState) {
    try {
      const dataToSave = {
        version: this.version,
        timestamp: Date.now(),
        audioData: audioState.audioData,
        currentEpisodeIndex: audioState.currentEpisodeIndex,
        currentTime: audioState.currentTime,
        volume: audioState.volume,
        volumeSlider: audioState.volumeSlider,
        playMode: audioState.playMode,
        playbackRate: audioState.playbackRate,
        isPlaying: false, // 重新进入时不自动播放
        // 只保存必要的音频信息，不保存整个 episodes 数组以节省空间
        audioInfo: {
          id: audioState.audioData.id,
          zhName: audioState.audioData.zhName,
          artist: audioState.audioData.artist,
          poster: audioState.audioData.poster,
          audioUrl: audioState.audioData.audioUrl,
          episodeCount: audioState.audioData.episodes?.length || 0
        }
      };

      localStorage.setItem(this.storageKey, JSON.stringify(dataToSave));
      console.log('音频状态已保存到本地存储');
    } catch (error) {
      console.error('保存音频状态失败:', error);
    }
  }

  /**
   * 从本地存储恢复音频播放状态
   * @returns {Object|null} 恢复的音频状态或 null
   */
  loadAudioState() {
    try {
      const savedData = localStorage.getItem(this.storageKey);
      if (!savedData) {
        console.log('没有找到保存的音频状态');
        return null;
      }

      const parsedData = JSON.parse(savedData);
      
      // 检查版本兼容性
      if (parsedData.version !== this.version) {
        console.warn('音频状态版本不兼容，清除旧数据');
        this.clearAudioState();
        return null;
      }

      // 检查数据是否过期（7天）
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 7天
      if (Date.now() - parsedData.timestamp > maxAge) {
        console.log('音频状态已过期，清除数据');
        this.clearAudioState();
        return null;
      }

      // 验证必要字段
      if (!parsedData.audioInfo || !parsedData.audioInfo.id) {
        console.warn('音频状态数据不完整');
        return null;
      }

      console.log('成功恢复音频状态:', parsedData);
      return parsedData;
    } catch (error) {
      console.error('恢复音频状态失败:', error);
      this.clearAudioState();
      return null;
    }
  }

  /**
   * 清除本地存储的音频状态
   */
  clearAudioState() {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('已清除本地音频状态');
    } catch (error) {
      console.error('清除音频状态失败:', error);
    }
  }

  /**
   * 检查是否有保存的音频状态
   * @returns {boolean}
   */
  hasAudioState() {
    try {
      const savedData = localStorage.getItem(this.storageKey);
      return !!savedData;
    } catch (error) {
      return false;
    }
  }

  /**
   * 获取保存的音频基本信息（不包含完整状态）
   * @returns {Object|null}
   */
  getAudioInfo() {
    try {
      const savedData = localStorage.getItem(this.storageKey);
      if (!savedData) return null;

      const parsedData = JSON.parse(savedData);
      return parsedData.audioInfo || null;
    } catch (error) {
      console.error('获取音频信息失败:', error);
      return null;
    }
  }

  /**
   * 更新播放时间（频繁调用，需要节流）
   * @param {string} currentTime - 当前播放时间
   */
  updateCurrentTime(currentTime) {
    try {
      const savedData = localStorage.getItem(this.storageKey);
      if (!savedData) return;

      const parsedData = JSON.parse(savedData);
      parsedData.currentTime = currentTime;
      parsedData.timestamp = Date.now();

      localStorage.setItem(this.storageKey, JSON.stringify(parsedData));
    } catch (error) {
      console.error('更新播放时间失败:', error);
    }
  }

  /**
   * 节流更新播放时间（避免频繁写入）
   */
  createThrottledTimeUpdate() {
    let lastUpdate = 0;
    const throttleDelay = 5000; // 5秒更新一次

    return (currentTime) => {
      const now = Date.now();
      if (now - lastUpdate >= throttleDelay) {
        this.updateCurrentTime(currentTime);
        lastUpdate = now;
      }
    };
  }

  /**
   * 保存播放偏好设置
   * @param {Object} preferences - 偏好设置
   */
  savePreferences(preferences) {
    try {
      const prefKey = `${this.storageKey}_preferences`;
      const dataToSave = {
        version: this.version,
        timestamp: Date.now(),
        ...preferences
      };

      localStorage.setItem(prefKey, JSON.stringify(dataToSave));
      console.log('播放偏好已保存');
    } catch (error) {
      console.error('保存播放偏好失败:', error);
    }
  }

  /**
   * 加载播放偏好设置
   * @returns {Object} 偏好设置
   */
  loadPreferences() {
    try {
      const prefKey = `${this.storageKey}_preferences`;
      const savedData = localStorage.getItem(prefKey);
      
      if (!savedData) {
        return this.getDefaultPreferences();
      }

      const parsedData = JSON.parse(savedData);
      
      // 检查版本兼容性
      if (parsedData.version !== this.version) {
        return this.getDefaultPreferences();
      }

      return {
        ...this.getDefaultPreferences(),
        ...parsedData
      };
    } catch (error) {
      console.error('加载播放偏好失败:', error);
      return this.getDefaultPreferences();
    }
  }

  /**
   * 获取默认偏好设置
   * @returns {Object}
   */
  getDefaultPreferences() {
    return {
      volume: 0.7,
      playMode: 1, // 1: 单曲循环, 2: 列表循环, 3: 随机播放
      playbackRate: 1.0,
      autoShowFloatingPlayer: true, // 是否自动显示浮动播放器
      rememberPosition: true // 是否记住播放位置
    };
  }

  /**
   * 获取存储使用情况
   * @returns {Object}
   */
  getStorageInfo() {
    try {
      const audioState = localStorage.getItem(this.storageKey);
      const preferences = localStorage.getItem(`${this.storageKey}_preferences`);
      
      return {
        hasAudioState: !!audioState,
        hasPreferences: !!preferences,
        audioStateSize: audioState ? audioState.length : 0,
        preferencesSize: preferences ? preferences.length : 0,
        totalSize: (audioState?.length || 0) + (preferences?.length || 0)
      };
    } catch (error) {
      console.error('获取存储信息失败:', error);
      return {
        hasAudioState: false,
        hasPreferences: false,
        audioStateSize: 0,
        preferencesSize: 0,
        totalSize: 0
      };
    }
  }

  /**
   * 清除所有相关的本地存储数据
   */
  clearAllData() {
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(`${this.storageKey}_preferences`);
      console.log('已清除所有音频相关数据');
    } catch (error) {
      console.error('清除数据失败:', error);
    }
  }
}

// 创建全局实例
const audioStorage = new AudioStorage();

export default audioStorage;
